/**
 * Business rules of the MaIA Try-On catalogue attributes (B4A-12582,
 * B4A-12660). Pure functions shared by the form component and its tests.
 * Product-level values live in `try_on_*`, tone-level values in
 * `variant_try_on_*` of public.product; the values mirror the CHECK constraints.
 */
export const TRY_ON_CATEGORIES = [
  'foundation',
  'lipstick',
  'blush',
  'concealer',
  'powder',
  'eyeshadow',
  'mascara',
  'eyebrows',
  'eyeliner',
  'bronzer',
  'contour',
  'highlighter',
  'lipliner'
] as const;
export type TryOnCategory = (typeof TRY_ON_CATEGORIES)[number];

export type TryOnFinish = 'matte' | 'satin' | 'gloss' | 'shimmer';
export type TryOnMode = 'photo' | 'live' | 'both';
export type TryOnCalibration =
  | 'mock_packaging_photo'
  | 'qa_adjusted'
  | 'brand_validated';

export interface TryOnOption<T extends string> {
  name: string;
  value: T;
}

export const TRY_ON_CATEGORY_OPTIONS: TryOnOption<TryOnCategory>[] = [
  { name: 'Base / protetor com cor (foundation)', value: 'foundation' },
  { name: 'Batom / gloss (lipstick)', value: 'lipstick' },
  { name: 'Blush', value: 'blush' },
  { name: 'Corretivo (concealer)', value: 'concealer' },
  { name: 'Pó facial (powder)', value: 'powder' },
  { name: 'Sombra (eyeshadow)', value: 'eyeshadow' },
  { name: 'Rímel (mascara)', value: 'mascara' },
  { name: 'Sobrancelha (eyebrows)', value: 'eyebrows' },
  { name: 'Delineador (eyeliner)', value: 'eyeliner' },
  { name: 'Bronzer', value: 'bronzer' },
  { name: 'Contorno (contour)', value: 'contour' },
  { name: 'Iluminador (highlighter)', value: 'highlighter' },
  { name: 'Lápis labial (lipliner)', value: 'lipliner' }
];

export const TRY_ON_FINISH_OPTIONS: TryOnOption<TryOnFinish>[] = [
  { name: 'Matte', value: 'matte' },
  { name: 'Acetinado (satin)', value: 'satin' },
  { name: 'Gloss', value: 'gloss' },
  { name: 'Cintilante (shimmer)', value: 'shimmer' }
];

export const TRY_ON_MODE_OPTIONS: TryOnOption<TryOnMode>[] = [
  { name: 'Foto', value: 'photo' },
  { name: 'Ao vivo', value: 'live' },
  { name: 'Foto e ao vivo', value: 'both' }
];

export const TRY_ON_CALIBRATION_OPTIONS: TryOnOption<TryOnCalibration>[] = [
  { name: 'Foto da embalagem (mock)', value: 'mock_packaging_photo' },
  { name: 'Ajustado pelo QA', value: 'qa_adjusted' },
  { name: 'Validado pela marca', value: 'brand_validated' }
];

export const TRY_ON_INTENSITY_MIN = 0;
export const TRY_ON_INTENSITY_MAX = 100;
export const TRY_ON_DEFAULT_MODE: TryOnMode = 'photo';

/**
 * Delivery switch. While false the admin always stores `try_on_mode = 'photo'`
 * and hides the mode selector. Flipped to true with the live SDK on the glam
 * PDP (B4A-14263): the admin chooses `photo | live | both` per product. The
 * thirteen categories are offered either way since the photo gateway accepts
 * all of them (glam.beauty PR #429).
 */
export const TRY_ON_LIVE_ENABLED = true;

export const TRY_ON_HEX_PATTERN = /^#[0-9A-F]{6}$/;

/**
 * Finishes each category renders. Categories absent from the map have a single
 * implicit finish (matte) and never store one. Source: MaIA Try-On SDK catalog.
 */
const FINISHES_BY_CATEGORY: Partial<Record<TryOnCategory, TryOnFinish[]>> = {
  lipstick: ['matte', 'satin', 'gloss'],
  eyeshadow: ['matte', 'satin', 'shimmer'],
  highlighter: ['satin', 'shimmer']
};

/**
 * Categories the photo render API (`/v1/try-on`) accepts: every SDK category
 * (the PDPs' allowlist mirrors `TRY_ON_CATEGORIES`; finish rules stay per
 * category in FINISHES_BY_CATEGORY). Kept as its own list so a future gateway
 * restriction is a one-line change.
 */
const PHOTO_CATEGORIES: readonly TryOnCategory[] = TRY_ON_CATEGORIES;

/** Colour/finish/intensity values of one tone (variant or single-SKU product). */
export interface TryOnRenderFields {
  enabled: boolean;
  color: string | null;
  finish: TryOnFinish | null;
  intensity: number | null;
}

export interface TryOnProductFields {
  category: TryOnCategory | null;
  enabled: boolean;
  mode: TryOnMode | null;
}

/**
 * Normalises user input to the stored format (#RRGGBB uppercase). Accepts
 * "c5906f", "#c5906f" and surrounding whitespace. Returns null for empty input
 * and undefined for input that cannot become a valid hex.
 */
export function normalizeTryOnHex(
  input: string | null | undefined
): string | null | undefined {
  const trimmed = (input ?? '').trim();
  if (!trimmed) return null;
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  const upper = withHash.toUpperCase();
  return TRY_ON_HEX_PATTERN.test(upper) ? upper : undefined;
}

export function isValidTryOnHex(value: string | null | undefined): boolean {
  return typeof value === 'string' && TRY_ON_HEX_PATTERN.test(value);
}

export function isValidTryOnIntensity(
  value: number | null | undefined
): boolean {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= TRY_ON_INTENSITY_MIN &&
    value <= TRY_ON_INTENSITY_MAX
  );
}

export function isTryOnCategory(value: unknown): value is TryOnCategory {
  return (
    typeof value === 'string' &&
    (TRY_ON_CATEGORIES as readonly string[]).includes(value)
  );
}

/** Finishes the admin may pick for the category (empty = field hidden). */
export function getTryOnFinishesForCategory(
  category: TryOnCategory | null | undefined
): TryOnFinish[] {
  return category ? (FINISHES_BY_CATEGORY[category] ?? []) : [];
}

export function getTryOnFinishOptionsForCategory(
  category: TryOnCategory | null | undefined
): TryOnOption<TryOnFinish>[] {
  const finishes = getTryOnFinishesForCategory(category);
  return TRY_ON_FINISH_OPTIONS.filter((option) =>
    finishes.includes(option.value)
  );
}

/** A finish is required exactly when the category offers more than one. */
export function isTryOnFinishRequired(
  category: TryOnCategory | null | undefined
): boolean {
  return getTryOnFinishesForCategory(category).length > 0;
}

export function isTryOnFinishAllowed(
  category: TryOnCategory | null | undefined,
  finish: TryOnFinish | null | undefined
): boolean {
  return !!finish && getTryOnFinishesForCategory(category).includes(finish);
}

/** Only foundation and lipstick can be rendered from a photo by the gateway. */
export function isTryOnPhotoSupported(
  category: TryOnCategory | null | undefined
): boolean {
  return !!category && PHOTO_CATEGORIES.includes(category);
}

/**
 * A category can be offered on the PDP when the current delivery renders it:
 * every category once the live SDK ships, photo-capable ones until then.
 */
export function isTryOnCategoryAvailable(
  category: TryOnCategory | null | undefined,
  liveEnabled: boolean = TRY_ON_LIVE_ENABLED
): boolean {
  if (!category) return false;
  return liveEnabled || isTryOnPhotoSupported(category);
}

/** Categories the admin may pick in the current delivery. */
export function getTryOnCategoryOptions(
  liveEnabled: boolean = TRY_ON_LIVE_ENABLED
): TryOnOption<TryOnCategory>[] {
  return TRY_ON_CATEGORY_OPTIONS.filter((option) =>
    isTryOnCategoryAvailable(option.value, liveEnabled)
  );
}

/** Modes the admin may pick for the category (photo only until live ships). */
export function getTryOnModeOptionsForCategory(
  category: TryOnCategory | null | undefined,
  liveEnabled: boolean = TRY_ON_LIVE_ENABLED
): TryOnOption<TryOnMode>[] {
  if (!liveEnabled)
    return TRY_ON_MODE_OPTIONS.filter((option) => option.value === 'photo');
  if (!category) return TRY_ON_MODE_OPTIONS;
  return isTryOnPhotoSupported(category)
    ? TRY_ON_MODE_OPTIONS
    : TRY_ON_MODE_OPTIONS.filter((option) => option.value === 'live');
}

export function isTryOnModeAllowed(
  category: TryOnCategory | null | undefined,
  mode: TryOnMode | null | undefined,
  liveEnabled: boolean = TRY_ON_LIVE_ENABLED
): boolean {
  return (
    !!mode &&
    getTryOnModeOptionsForCategory(category, liveEnabled).some(
      (option) => option.value === mode
    )
  );
}

/**
 * Mode to store for the category: keeps the current one when allowed,
 * otherwise the only option left (photo before live ships, live for the
 * live-only categories afterwards) or the default.
 */
export function coerceTryOnMode(
  category: TryOnCategory | null | undefined,
  mode: TryOnMode | null | undefined,
  liveEnabled: boolean = TRY_ON_LIVE_ENABLED
): TryOnMode {
  if (isTryOnModeAllowed(category, mode, liveEnabled)) return mode as TryOnMode;
  const options = getTryOnModeOptionsForCategory(category, liveEnabled);
  return options.length === 1 ? options[0].value : TRY_ON_DEFAULT_MODE;
}

/**
 * A tone is renderable when it is enabled, has a colour and an intensity, and
 * a finish allowed by the category when the category requires one.
 */
export function isTryOnRowRenderable(
  row: TryOnRenderFields,
  product: TryOnProductFields
): boolean {
  if (!row.enabled) return false;
  if (!isValidTryOnHex(row.color)) return false;
  if (!isValidTryOnIntensity(row.intensity)) return false;
  if (
    isTryOnFinishRequired(product.category) &&
    !isTryOnFinishAllowed(product.category, row.finish)
  )
    return false;
  return true;
}

/**
 * Reason (pt-BR, shown inline) why try_on_enabled cannot be turned on for the
 * product, or null when every business rule is satisfied.
 */
export function getTryOnEnableBlockReason(
  product: TryOnProductFields,
  rows: TryOnRenderFields[],
  liveEnabled: boolean = TRY_ON_LIVE_ENABLED
): string | null {
  if (!product.category) return 'Selecione a categoria da prova virtual.';
  if (!isTryOnCategoryAvailable(product.category, liveEnabled)) {
    return 'Esta categoria só será renderizada com a prova ao vivo (próxima entrega): selecione base ou batom.';
  }
  if (!isTryOnModeAllowed(product.category, product.mode, liveEnabled)) {
    return isTryOnPhotoSupported(product.category)
      ? 'Selecione o modo da prova virtual.'
      : 'Esta categoria só é renderizada ao vivo: selecione o modo "Ao vivo".';
  }
  if (!rows.some((row) => isTryOnRowRenderable(row, product))) {
    return isTryOnFinishRequired(product.category)
      ? 'É necessário ao menos um tom habilitado com cor, intensidade e acabamento.'
      : 'É necessário ao menos um tom habilitado com cor e intensidade.';
  }
  return null;
}

/**
 * Field-level errors of a tone the admin marked as enabled. Disabled tones may
 * be left incomplete, but never invalid.
 */
export function getTryOnRowErrors(
  row: TryOnRenderFields,
  product: TryOnProductFields
): string[] {
  const errors: string[] = [];
  if (row.color !== null && !isValidTryOnHex(row.color))
    errors.push('Cor inválida: use o formato #RRGGBB.');
  if (row.intensity !== null && !isValidTryOnIntensity(row.intensity))
    errors.push('Intensidade deve ser um inteiro entre 0 e 100.');
  if (
    row.finish !== null &&
    isTryOnFinishRequired(product.category) &&
    !isTryOnFinishAllowed(product.category, row.finish)
  )
    errors.push('Acabamento não disponível para esta categoria.');
  if (!row.enabled) return errors;
  if (row.color === null) errors.push('Informe a cor para habilitar o tom.');
  if (row.intensity === null)
    errors.push('Informe a intensidade para habilitar o tom.');
  if (isTryOnFinishRequired(product.category) && row.finish === null)
    errors.push('Informe o acabamento (obrigatório para esta categoria).');
  return errors;
}

/**
 * Hasura column payload for a tone. `try_on_enabled` is the tone switch on a
 * variant row; on a single-SKU product the same column is the product switch,
 * written by `buildTryOnProductSetInput`, so it is omitted here.
 */
export interface TryOnRowSetInput {
  try_on_enabled?: boolean;
  variant_try_on_color: string | null;
  variant_try_on_finish: TryOnFinish | null;
  variant_try_on_intensity: number | null;
  variant_try_on_calibration: TryOnCalibration | null;
}

/**
 * Builds the `_set` payload of a tone. The finish is only persisted when the
 * category offers a choice and the value is one of its finishes; otherwise
 * the column is cleared so it never carries a stale value.
 */
export function buildTryOnRowSetInput(
  category: TryOnCategory | null,
  row: TryOnRenderFields & { calibration: TryOnCalibration | null },
  isVariant: boolean
): TryOnRowSetInput {
  return {
    ...(isVariant ? { try_on_enabled: row.enabled } : {}),
    variant_try_on_color: row.color,
    variant_try_on_finish: isTryOnFinishAllowed(category, row.finish)
      ? row.finish
      : null,
    variant_try_on_intensity: row.intensity,
    variant_try_on_calibration: row.calibration
  };
}

export interface TryOnProductSetInput {
  try_on_category: TryOnCategory | null;
  try_on_enabled: boolean;
  try_on_mode: TryOnMode;
  try_on_disclaimer: string | null;
  /** Palette tones; written by the form on single-SKU products (see §shades). */
  try_on_shades?: TryOnShade[] | null;
}

/**
 * Builds the `_set` payload of the parent row. The mode is coerced to one the
 * category supports and the business rules are evaluated against that
 * persisted mode; the toggle is forced off when they are not met, so a stale
 * `true` never reaches the PDP.
 */
export function buildTryOnProductSetInput(
  product: TryOnProductFields & { disclaimer: string | null },
  rows: TryOnRenderFields[],
  liveEnabled: boolean = TRY_ON_LIVE_ENABLED
): TryOnProductSetInput {
  const mode = coerceTryOnMode(product.category, product.mode, liveEnabled);
  const persisted = { ...product, mode };
  const blocked =
    getTryOnEnableBlockReason(persisted, rows, liveEnabled) !== null;
  const disclaimer = product.disclaimer?.trim() || null;
  return {
    try_on_category: product.category,
    try_on_enabled: blocked ? false : product.enabled,
    try_on_mode: mode,
    try_on_disclaimer: disclaimer
  };
}

export function getTryOnOptionName<T extends string>(
  options: TryOnOption<T>[],
  value: T | null | undefined
): string {
  return options.find((option) => option.value === value)?.name ?? '—';
}

/*
 * Palette shades (`product.try_on_shades`, JSONB array). A single-SKU parent
 * (palette) can offer N try-on tones without variant rows. Precedence read by
 * the PDPs: variant tones > try_on_shades (>= 1 shade) > legacy
 * `variant_try_on_*` of the parent row. Shades are only editable on products
 * without variants; every stored shade is published (no per-shade switch).
 */
export const TRY_ON_SHADES_MAX = 24;
export const TRY_ON_SHADE_NAME_MAX = 80;
export const TRY_ON_SHADE_SLUG_MAX = 64;
export const TRY_ON_SHADE_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Element of the `try_on_shades` JSON array, as persisted. `slug` is the
 * identity the PDPs use for the render (`variant_slug`, shades have no EAN);
 * the admin derives it from the name and never shows it.
 */
export interface TryOnShade {
  slug: string;
  name: string;
  color: string;
  finish: TryOnFinish | null;
  intensity: number;
  calibration?: TryOnCalibration;
}

/** Shade as edited in the form: every field may still be empty. */
export interface TryOnShadeDraft {
  /** Stored slug, kept across renames; null for a new shade. */
  slug: string | null;
  name: string | null;
  color: string | null;
  finish: TryOnFinish | null;
  intensity: number | null;
  calibration: TryOnCalibration | null;
}

const TRY_ON_FINISHES: readonly TryOnFinish[] = TRY_ON_FINISH_OPTIONS.map(
  (option) => option.value
);
const TRY_ON_CALIBRATIONS: readonly TryOnCalibration[] =
  TRY_ON_CALIBRATION_OPTIONS.map((option) => option.value);

function isTryOnFinish(value: unknown): value is TryOnFinish {
  return (
    typeof value === 'string' && TRY_ON_FINISHES.includes(value as TryOnFinish)
  );
}

function isTryOnCalibration(value: unknown): value is TryOnCalibration {
  return (
    typeof value === 'string' &&
    TRY_ON_CALIBRATIONS.includes(value as TryOnCalibration)
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function isValidTryOnShadeSlug(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length <= TRY_ON_SHADE_SLUG_MAX &&
    TRY_ON_SHADE_SLUG_PATTERN.test(value)
  );
}

/**
 * Slug derived from the display name: accents stripped, lowercase, runs of
 * anything else collapsed to a single dash, cut at TRY_ON_SHADE_SLUG_MAX.
 * A name with no usable character falls back to "tom".
 */
export function slugifyTryOnShadeName(name: string | null | undefined): string {
  const slug = (name ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, TRY_ON_SHADE_SLUG_MAX)
    .replace(/-+$/g, '');
  return slug || 'tom';
}

/** Appends -2, -3… until the slug is unused, keeping it within the max length. */
function uniqueShadeSlug(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  for (let suffix = 2; ; suffix++) {
    const tail = `-${suffix}`;
    const candidate =
      base.slice(0, TRY_ON_SHADE_SLUG_MAX - tail.length).replace(/-+$/g, '') +
      tail;
    if (!taken.has(candidate)) return candidate;
  }
}

/**
 * Converts the raw JSONB column into form drafts. Never throws: a column that
 * is not an array yields no shades; fields of the wrong type become null so
 * the form shows them as missing and the admin fixes them before saving.
 * Colours are kept verbatim (trimmed) so an invalid stored hex is surfaced.
 */
export function parseTryOnShades(value: unknown): TryOnShadeDraft[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord).map((item) => ({
    slug: isValidTryOnShadeSlug(item['slug']) ? item['slug'] : null,
    name: typeof item['name'] === 'string' ? item['name'].trim() || null : null,
    color:
      typeof item['color'] === 'string' ? item['color'].trim() || null : null,
    finish: isTryOnFinish(item['finish']) ? item['finish'] : null,
    intensity: typeof item['intensity'] === 'number' ? item['intensity'] : null,
    calibration: isTryOnCalibration(item['calibration'])
      ? item['calibration']
      : null
  }));
}

export function tryOnShadeToRenderFields(
  shade: TryOnShadeDraft
): TryOnRenderFields {
  return {
    enabled: true,
    color: shade.color,
    finish: shade.finish,
    intensity: shade.intensity
  };
}

/** Legacy single colour of the parent row (`variant_try_on_*`). */
export interface TryOnLegacyToneFields {
  variant_try_on_color: string | null;
  variant_try_on_finish: TryOnFinish | null;
  variant_try_on_intensity: number | null;
  variant_try_on_calibration: TryOnCalibration | null;
}

/**
 * The legacy colour shown as the single item of the tone list. It has no name
 * in the database, so the name starts empty (the form shows "Tom 1").
 */
export function legacyToneToShadeDraft(
  row: TryOnLegacyToneFields
): TryOnShadeDraft {
  return {
    slug: null,
    name: null,
    color: row.variant_try_on_color,
    finish: row.variant_try_on_finish,
    intensity: row.variant_try_on_intensity,
    calibration: row.variant_try_on_calibration
  };
}

/**
 * Tones of a single-SKU product, as loaded: the stored palette when it has
 * items, otherwise the legacy colour as the only tone.
 */
export function resolveSingleSkuTones(
  row: TryOnLegacyToneFields,
  storedShades: unknown
): TryOnShadeDraft[] {
  const shades = parseTryOnShades(storedShades);
  return shades.length > 0 ? shades : [legacyToneToShadeDraft(row)];
}

function normalizeShadeName(name: string | null): string {
  return (name ?? '').trim().toLowerCase();
}

/**
 * Field-level errors of one shade. Shades have no switch, so every stored
 * shade must be complete and renderable for the category. The name is only
 * required when the list has several tones (`nameRequired`): a single tone is
 * stored in the legacy columns, which carry no name.
 */
export function getTryOnShadeErrors(
  shade: TryOnShadeDraft,
  category: TryOnCategory | null,
  nameRequired = true
): string[] {
  const errors: string[] = [];
  const name = (shade.name ?? '').trim();
  if (!name && nameRequired) errors.push('Informe o nome do tom.');
  else if (name.length > TRY_ON_SHADE_NAME_MAX)
    errors.push(
      `Nome do tom deve ter até ${TRY_ON_SHADE_NAME_MAX} caracteres.`
    );
  if (shade.color === null) errors.push('Informe a cor do tom.');
  else if (!isValidTryOnHex(shade.color))
    errors.push('Cor inválida: use o formato #RRGGBB.');
  if (shade.intensity === null) errors.push('Informe a intensidade do tom.');
  else if (!isValidTryOnIntensity(shade.intensity))
    errors.push('Intensidade deve ser um inteiro entre 0 e 100.');
  if (isTryOnFinishRequired(category)) {
    if (shade.finish === null)
      errors.push('Informe o acabamento (obrigatório para esta categoria).');
    else if (!isTryOnFinishAllowed(category, shade.finish))
      errors.push('Acabamento não disponível para esta categoria.');
  }
  return errors;
}

/**
 * List-level errors: capped at TRY_ON_SHADES_MAX and distinct names (the PDP
 * swatch picker identifies a shade by its name). Products with variants are
 * blocked upstream: canAddTryOnShade refuses new shades and
 * buildTryOnShadesInput clears the column.
 */
export function getTryOnShadesListErrors(shades: TryOnShadeDraft[]): string[] {
  const errors: string[] = [];
  if (shades.length === 0) return errors;
  if (shades.length > TRY_ON_SHADES_MAX)
    errors.push(`Máximo de ${TRY_ON_SHADES_MAX} tons por produto.`);
  const names = shades.map((shade) => normalizeShadeName(shade.name));
  const hasDuplicate = names.some(
    (name, index) => name !== '' && names.indexOf(name) !== index
  );
  if (hasDuplicate) errors.push('Os nomes dos tons devem ser únicos.');
  return errors;
}

export function canAddTryOnShade(count: number, hasVariants: boolean): boolean {
  return !hasVariants && count < TRY_ON_SHADES_MAX;
}

/**
 * Value of `try_on_shades` to persist. Products with variants never store
 * shades (stale data is cleared); an empty list is stored as null so the PDPs
 * fall back to the legacy single colour. Callers must have validated the
 * drafts with getTryOnShadeErrors/getTryOnShadesListErrors: an incomplete
 * shade is dropped here rather than written half-filled. The stored slug is
 * kept when valid (identity survives a rename); otherwise it is derived from
 * the name. Slugs are made unique within the product.
 */
export function buildTryOnShadesInput(
  category: TryOnCategory | null,
  shades: TryOnShadeDraft[],
  hasVariants: boolean
): TryOnShade[] | null {
  if (hasVariants || shades.length === 0) return null;
  const taken = new Set<string>();
  const persisted = shades
    .filter((shade) => getTryOnShadeErrors(shade, category).length === 0)
    .slice(0, TRY_ON_SHADES_MAX)
    .map((shade): TryOnShade => {
      const name = (shade.name as string).trim();
      const base = isValidTryOnShadeSlug(shade.slug)
        ? shade.slug
        : slugifyTryOnShadeName(name);
      const slug = uniqueShadeSlug(base, taken);
      taken.add(slug);
      return {
        slug,
        name,
        color: shade.color as string,
        finish: isTryOnFinishAllowed(category, shade.finish)
          ? shade.finish
          : null,
        intensity: shade.intensity as number,
        ...(shade.calibration ? { calibration: shade.calibration } : {})
      };
    });
  return persisted.length > 0 ? persisted : null;
}
/**
 * Whether the legacy columns to persist equal the ones loaded from the
 * database. The parent row is only written when they differ, so the decision
 * is derived from stored state + current list, never from form dirtiness:
 * a stored palette of one shade (legacy columns empty) always lands in the
 * legacy columns, whatever field the admin touched.
 */
export function isSameLegacyTone(
  row: TryOnRowSetInput,
  loaded: TryOnLegacyToneFields
): boolean {
  return (
    row.variant_try_on_color === loaded.variant_try_on_color &&
    row.variant_try_on_finish === loaded.variant_try_on_finish &&
    row.variant_try_on_intensity === loaded.variant_try_on_intensity &&
    row.variant_try_on_calibration === loaded.variant_try_on_calibration
  );
}

/** Payloads of a single-SKU product's tone list (legacy columns + JSONB). */
export interface TryOnSingleSkuToneSetInputs {
  /** `_set` of the parent row: the single tone, or the first tone mirrored. */
  row: TryOnRowSetInput;
  /** `try_on_shades`: null for 0–1 tones, the whole list for 2+. */
  shades: TryOnShade[] | null;
}

const EMPTY_TONE: TryOnRenderFields & { calibration: TryOnCalibration | null } =
  {
    enabled: true,
    color: null,
    finish: null,
    intensity: null,
    calibration: null
  };

/**
 * Persistence of the unified tone list of a single-SKU product, without any
 * schema change: one tone lives in `variant_try_on_*` exactly as before (so
 * existing products save the same payload) and `try_on_shades` stays null;
 * two or more tones are stored in `try_on_shades` and the first one is
 * mirrored into the legacy columns for readers that only know those.
 */
export function buildSingleSkuToneSetInputs(
  category: TryOnCategory | null,
  shades: TryOnShadeDraft[]
): TryOnSingleSkuToneSetInputs {
  const first = shades[0];
  const row = buildTryOnRowSetInput(
    category,
    first
      ? { ...tryOnShadeToRenderFields(first), calibration: first.calibration }
      : EMPTY_TONE,
    false
  );
  return {
    row,
    shades:
      shades.length > 1 ? buildTryOnShadesInput(category, shades, false) : null
  };
}
